import { useCallback } from "react";
import { subscribeToPricingSettings } from "../firebase/services";
import { pricingService } from "@/lib/booking/pricingService";
import { PricingSettings } from "../types";
import { useSubscription, type Subscribe } from "./useSubscription";

/**
 * React hook a központi árazási és nyitvatartás kezelő szolgáltatás használatához
 *
 * A settings/pricing figyelőjére épül (nem egyszeri lekérésre): az első értéket a lokális
 * cache adja, hálózati hiba után magától újracsatlakozik. Minden beérkező érték a
 * PricingService singletonba is bekerül, így a segédfüggvények (ár, nyitvatartás) mindig a
 * legfrissebb adattal számolnak.
 */
export function usePricingService() {
  const subscribe = useCallback<Subscribe<PricingSettings>>(
    (onChange, onError) =>
      subscribeToPricingSettings((data) => onChange(pricingService.applyPricing(data)), onError),
    [],
  );
  // A singleton cache-éből indulunk: oldalváltás után a már ismert árak azonnal
  // kirajzolódnak, a frissítés a háttérben megy - nem villan fel töltésjelző
  const { data: pricing, loading, error } = useSubscription(subscribe, {
    initialData: pricingService.getPricing(),
  });

  return {
    // Állapot
    pricing,
    loading,
    error,

    // Segédfüggvények
    /**
     * Napi nyitvatartás lekérdezése
     */
    getDaySchedule: (date: Date) => pricingService.getDaySchedule(date),

    /**
     * Ár kiszámítása
     */
    calculateBookingPrice: (
      date: Date,
      startTime: string,
      endTime: string,
      laneCount: number,
      shoesRental: number
    ): number => {
      return pricingService.calculateBookingPrice(
        date,
        startTime,
        endTime,
        laneCount,
        shoesRental
      );
    },

    /**
     * Szükséges pályák száma
     */
    getRequiredLanes: (date: Date, guestCount: number): number => {
      return pricingService.getRequiredLanes(date, guestCount);
    },

    /**
     * Dátum választhatóságának ellenőrzése
     */
    isDateSelectable: (date: Date): boolean => {
      return pricingService.isDateSelectable(date);
    },

    /**
     * Vendégszám korlátozások
     */
    getGuestLimits: (date: Date) => {
      return pricingService.getGuestLimits(date);
    }
  };
}

